import { useEffect, useState, type JSX } from 'react';
import type { TakiConfig } from '@taki/runtime-config';
import {
  Button,
  SettingsActions,
  SettingsCopyText,
  SettingsInfo,
  SettingsMenu,
  SettingsMessage,
  SettingsRow,
  SettingsSection,
  SettingsTable,
  Switch,
  useIaSettingsPort,
  type IaConnectionView,
} from '@taki/ui';

import { ConnecteursSection } from './ConnecteursSection.js';
import { WhatsappSection } from './WhatsappSection.js';

/** Le domaine affiche est celui de la page : un domaine personnalise garde la session du membre pendant OAuth. */
function endpoint(config: TakiConfig): string {
  const emulator = config.emulators?.functions;
  return emulator === undefined
    ? `${window.location.origin}/mcp`
    : config.emulators?.mcpPublicOrigin
      ? `${config.emulators.mcpPublicOrigin}/mcp`
      : config.emulators?.hosting
      ? `http://${config.emulators.hosting.host}:${config.emulators.hosting.port}/mcp`
      : 'Adresse MCP locale absente de la configuration.';
}

function date(value: Date | null): string {
  return value === null ? 'Date inconnue' : new Intl.DateTimeFormat('fr-LU', {
    dateStyle: 'medium', timeStyle: 'short',
  }).format(value);
}

/**
 * LES SECTIONS IA DE PARAMETRES > CONVERSATIONS (ex-onglet « IA », renomme le
 * 06/10/2026) : la fenetre de chat (son lanceur), Lia (acces et modele),
 * WhatsApp, les connecteurs mail et agenda, l'assistant externe (adresse MCP)
 * et les connexions MCP. Rendues
 * en FRAGMENT : c'est `ConversationsSettingsPanel` qui pose le cadre et les
 * fait suivre la section Canaux. Ordre et portee : `STRUCTURE.md` du chantier,
 * resume dans `docs/GABARIT-ECRAN.md` (hub Parametres).
 */
export function SectionsLia({ config }: { config: TakiConfig }): JSX.Element {
  const port = useIaSettingsPort();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /* L'erreur du lanceur s'affiche dans SA section (Fenetre de chat), pas sous Lia. */
  const [launcherError, setLauncherError] = useState<string | null>(null);
  const [connections, setConnections] = useState<IaConnectionView[]>([]);
  const [showAll, setShowAll] = useState(false);
  const [revoking, setRevoking] = useState<string | null>(null);

  const refresh = async (): Promise<void> => {
    if (!port?.ready) return;
    try {
      setConnections((await port.connections(showAll && port.canManage)).filter((item) => item.revokedAt === null));
      setError(null);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    }
  };

  useEffect(() => { void refresh(); }, [port?.canManage, port?.enabled, port?.ready, showAll]);

  const revoke = async (id: string): Promise<void> => {
    if (!port) return;
    setRevoking(id);
    setError(null);
    try {
      await port.revokeConnection(id);
      setConnections((items) => items.filter((item) => item.id !== id));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally { setRevoking(null); }
  };

  const toggle = async (enabled: boolean): Promise<void> => {
    if (port === null || !port.canManage) return;
    setBusy(true);
    setError(null);
    try { await port.setEnabled(enabled); }
    catch (cause) { setError(cause instanceof Error ? cause.message : String(cause)); }
    finally { setBusy(false); }
  };

  const changeLauncher = async (launcher: 'mascot' | 'bubble'): Promise<void> => {
    if (port === null || !port.canManage) return;
    setBusy(true);
    setLauncherError(null);
    try { await port.setLauncher(launcher); }
    catch (cause) { setLauncherError(cause instanceof Error ? cause.message : String(cause)); }
    finally { setBusy(false); }
  };

  const changeModel = async (model: string): Promise<void> => {
    if (port === null || !port.canManage) return;
    setBusy(true);
    setError(null);
    try { await port.setModel(model === port.defaultModel ? null : model); }
    catch (cause) { setError(cause instanceof Error ? cause.message : String(cause)); }
    finally { setBusy(false); }
  };

  const models = port?.models ?? [];
  const model = port?.model ?? port?.defaultModel ?? '';

  const verrouProprietaire = port?.canManage ? undefined : 'Propriétaire uniquement';

  return <>
    <SettingsSection title="Fenêtre de chat" contextLabel="Organisation">
      <SettingsRow label="Bouton de Lia" lock={verrouProprietaire}>
        <SettingsMenu label="Lanceur de Lia" value={port?.launcher ?? 'mascot'}
          options={[{ value: 'mascot', label: 'Mascotte' }, { value: 'bubble', label: 'Bulle' }]}
          disabled={port === null || !port.ready || !port.canManage || busy}
          onChange={(value) => void changeLauncher(value === 'bubble' ? 'bubble' : 'mascot')} />
      </SettingsRow>
      {launcherError === null ? null : <SettingsMessage tone="error">{launcherError}</SettingsMessage>}
    </SettingsSection>
    <SettingsSection title="Assistant" contextLabel="Organisation">
      <SettingsRow label="Activer Lia" lock={verrouProprietaire}>
        <Switch checked={port?.enabled ?? false} onChange={(value) => void toggle(value)}
          locked={port === null || !port.ready || !port.canManage || busy}
          lockedReason={!port?.canManage ? 'Propriétaire uniquement' : undefined}
          aria-label="Autoriser l'IA" />
      </SettingsRow>
      <SettingsRow label="Modèle" lock={verrouProprietaire}>
        <SettingsMenu label="Modèle de Lia" value={model}
          options={models.map((choice) => ({
            value: choice.id,
            label: `${choice.label}${choice.id === port?.defaultModel ? ' (par défaut)' : ''} · ${choice.speed}`,
          }))}
          disabled={port === null || !port.ready || !port.canManage || busy}
          onChange={(value) => void changeModel(value)} />
      </SettingsRow>
      {error === null ? null : <SettingsMessage tone="error">{error}</SettingsMessage>}
      <SettingsInfo>DeepSeek par défaut. Les autres modèles peuvent coûter plus.</SettingsInfo>
      <SettingsInfo>Tokens facturés à part du forfait MyTaki.</SettingsInfo>
    </SettingsSection>
    <WhatsappSection port={port?.whatsapp} aiEnabled={port?.enabled ?? false} />
    <ConnecteursSection port={port?.connecteurs} />
    <SettingsSection title="Assistant externe" contextLabel="Votre compte">
      <SettingsInfo>Claude : Ajouter un connecteur → Copier le lien → Laisser les champs cochés → Se connecter.</SettingsInfo>
      <SettingsRow label="Adresse MCP"><SettingsCopyText value={endpoint(config)} label="Copier l’adresse MCP" /></SettingsRow>
    </SettingsSection>
    <SettingsSection title="Connexions" contextLabel={showAll ? 'Organisation' : 'Votre compte'}>
      {port?.canManage ? <SettingsRow label="Afficher">
        <SettingsMenu label="Connexions affichées" value={showAll ? 'organisation' : 'miennes'}
          options={[{ value: 'miennes', label: 'Les miennes' }, { value: 'organisation', label: 'Toute l’organisation' }]}
          onChange={(value) => setShowAll(value === 'organisation')} />
      </SettingsRow> : null}
      <SettingsTable
        label="Connexions"
        columns={[
          { label: 'Connexion' },
          ...(showAll ? [{ label: 'Membre' }] : []),
          { label: 'Dernier accès' },
          { label: 'Révoquer', cache: true, fin: true },
        ]}
        empty="Aucune connexion."
        rows={connections.map((item) => ({
          id: item.id,
          cells: [
            <span key="client" className="font-semibold text-ink-700">{item.clientName}</span>,
            ...(showAll ? [item.email ?? item.uid] : []),
            date(item.lastSeenAt ?? item.connectedAt),
            <Button key="revoquer" variant="secondary" size="sm" loading={revoking === item.id} onClick={() => void revoke(item.id)} aria-label={`Révoquer ${item.clientName}`}>Révoquer</Button>,
          ],
        }))}
      />
      <SettingsActions><Button variant="secondary" size="sm" onClick={() => void refresh()}>Actualiser</Button></SettingsActions>
    </SettingsSection>
  </>;
}
